import React from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	VStack,
	HStack,
	Text,
	Box,
	Icon,
} from '@chakra-ui/react';
import { FiClock, FiAlertCircle } from 'react-icons/fi';
import Ticks from './Ticks';
import { full } from './time';

/**
 * "Message info" — every delivery status Meta reported, with times.
 *
 * `statusHistory` has been stored on every message since day one and never
 * shown; a support question about a message that "never arrived" is
 * unanswerable without it. Read/Delivered/Sent in WhatsApp's order, newest
 * first, plus the failure code and title when there is one.
 */
const ORDER = ['READ', 'DELIVERED', 'SENT', 'ACCEPTED', 'FAILED', 'PENDING_UNKNOWN'];
const TITLE = {
	READ: 'Read',
	DELIVERED: 'Delivered',
	SENT: 'Sent',
	ACCEPTED: 'Accepted by WhatsApp',
	FAILED: 'Failed',
	PENDING_UNKNOWN: 'Pending',
};

export default function MessageInfoDialog({ isOpen, onClose, message, senderName }) {
	const rows = React.useMemo(() => {
		const history = [...(message?.statusHistory ?? [])];
		history.sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));
		return history;
	}, [message]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
			<ModalOverlay />
			<ModalContent bg="wa.menu" color="wa.ink">
				<ModalHeader fontSize="md">Message info</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb="5">
					<Box
						bg="wa.outgoing"
						borderRadius="7.5px"
						px="3"
						py="2"
						mb="4"
						fontSize="sm"
						whiteSpace="pre-wrap"
						wordBreak="break-word"
						maxH="120px"
						overflowY="auto"
					>
						{message?.content || `[${message?.type ?? 'message'}]`}
					</Box>
					<VStack align="stretch" spacing="3">
						{senderName && (
							<Text fontSize="xs" color="wa.inkMuted">
								Sent by {senderName}
							</Text>
						)}
						{rows.length === 0 && (
							<HStack spacing="2">
								<Icon as={FiClock} color="wa.inkMuted" />
								<Text fontSize="sm">No delivery updates yet</Text>
							</HStack>
						)}
						{rows.map((row, i) => (
							<HStack key={`${row.status}-${i}`} justify="space-between" align="flex-start">
								<HStack spacing="2" align="center">
									{row.status === 'FAILED' ? (
										<Icon as={FiAlertCircle} color="danger" />
									) : (
										<Ticks status={row.status} />
									)}
									<Box>
										<Text fontSize="sm">{TITLE[row.status] ?? row.status}</Text>
										{row.title && (
											<Text fontSize="xs" color="wa.inkMuted">
												{row.code ? `${row.code} · ` : ''}
												{row.title}
											</Text>
										)}
									</Box>
								</HStack>
								<Text fontSize="xs" color="wa.inkMuted" whiteSpace="nowrap">
									{full(row.at)}
								</Text>
							</HStack>
						))}
					</VStack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
